import { createFileRoute, Link } from "@tanstack/react-router";
import { motion } from "framer-motion";
import { Calculator, FileSearch, Receipt, Briefcase, ArrowRight, CheckCircle2, Phone, ShieldCheck, TrendingUp, Users, Clock } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { img } from "@/lib/images";
import { site } from "@/lib/site";
import { SmartImage } from "@/components/SmartImage";
import { canonicalLinks, routeMeta } from "@/lib/seo";

export const Route = createFileRoute("/services/")({
  head: () => ({
    meta: [
      ...routeMeta("/services"),
      { title: "CPA Services in Winter Garden | CCOB & Company" },
      { name: "description", content: "Explore accounting, auditing, taxation and IRS representation services from CCOB And Company PA in Winter Garden, FL." },
      { property: "og:title", content: "CPA Services — CCOB And Company PA" },
      { property: "og:description", content: "Accounting, Auditing, Taxation and IRS Representation." },
    ],
    links: canonicalLinks("/services"),
  }),
  component: ServicesPage,
});

const services = [
  {
    title: "Accounting Services",
    tagline: "Clean books. Clear decisions.",
    desc: "Bookkeeping, payroll, financial analysis, and reporting designed for owners who want clarity, not clutter. We keep your numbers current so you can lead with confidence.",
    img: img.accounting,
    icon: Calculator,
    to: "/services/accounting",
    items: [
      "Monthly Bookkeeping & Reconciliation",
      "Payroll Processing & Compliance",
      "Financial Statement Preparation",
      "Cash Flow & Budget Analysis",
      "QuickBooks Setup & Training",
      "Business Performance Reporting",
    ],
  },
  {
    title: "Financial Audit Services",
    tagline: "Independent. Thorough. Trusted.",
    desc: "Independent financial audits, internal reviews, compliance, and reporting audits — focused purely on the integrity of your financial information. Kept distinct from our tax practice so each is done right.",
    img: img.auditing,
    icon: FileSearch,
    to: "/services/auditing",
    items: [
      "Financial Statement Audits",
      "Internal Audits",
      "Compliance Reviews",
      "Accounting & Reporting Audits",
      "Business Financial Assessments",
    ],
  },
  {
    title: "Taxation Services",
    tagline: "Plan ahead. Pay less. Sleep better.",
    desc: "Year-round tax planning, preparation, filings, IRS audit support, resolution, and consulting — handled by a CPA who's there when you need them. Separate from our financial audit work.",
    img: img.tax,
    icon: Receipt,
    to: "/services/taxation",
    items: [
      "IRS Audit Support",
      "Tax Preparation",
      "Tax Filings",
      "Tax Planning",
      "Tax Resolution & Compliance",
      "Tax Consulting",
    ],
  },
  {
    title: "Part-Time CFO Services",
    tagline: "CFO leadership. Without the full-time price tag.",
    desc: "Strategic financial leadership for growing businesses — budgeting, profitability, cash flow, financing, and long-term planning — at a fraction of the cost of a full-time CFO.",
    img: img.accounting,
    icon: Briefcase,
    to: "/services/part-time-cfo",
    items: [
      "Budgeting & Profitability Analysis",
      "Cash Flow Management",
      "Tax-Saving Strategies",
      "Financial Controls & Oversight",
      "Financing & Banking Relationships",
      "Long-Term Business Planning",
      "Mergers & Acquisitions Support",
      "Accounting Staff Management",
    ],
  },
] as const;

const industries = [
  "Real Estate", "Healthcare", "Construction", "Hospitality",
  "Nonprofits", "Professional Services", "Restaurants", "Retail",
];

const process = [
  { step: "01", t: "Discovery Call", d: "A free 30-minute conversation to understand your goals, current setup, and where we can help most." },
  { step: "02", t: "Service Scoping", d: "We map the right mix of accounting, audit, or tax services and share a transparent, fixed-scope proposal." },
  { step: "03", t: "Onboarding", d: "Secure document collection, software setup, and a kickoff session — handled with care, not friction." },
  { step: "04", t: "Ongoing Partnership", d: "Proactive check-ins, year-round availability, and strategic guidance — not just at tax time." },
];

const why = [
  [ShieldCheck, "CPA Licensed & Insured"],
  [Clock, "30+ Years Experience"],
  [Users, "Personal, Direct Access"],
  [TrendingUp, "Strategic, Not Just Compliant"],
];

function ServicesPage() {
  const fadeUp = { initial: { opacity: 0, y: 24 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: "-80px" }, transition: { duration: 0.6 } };
  return (
    <>
      <PageHero eyebrow="What We Do" title="Comprehensive CPA Services for Individuals & Businesses" subtitle="Three pillars — accounting, auditing, and taxation — delivered by a CPA you can actually reach." image={img.heroServices} />

      {/* INTRO */}
      <section className="py-20">
        <div className="container-x grid lg:grid-cols-12 gap-10 items-end">
          <motion.div {...fadeUp} className="lg:col-span-7">
            <div className="text-gold uppercase tracking-[0.25em] text-xs mb-4">Our Approach</div>
            <h2 className="font-display text-4xl md:text-5xl text-primary leading-[1.05]">
              Practical CPA work, <span className="italic text-gold">rooted in relationships</span>.
            </h2>
            <div className="gold-line mt-5" />
          </motion.div>
          <motion.p {...fadeUp} className="lg:col-span-5 text-muted-foreground leading-relaxed">
            Every engagement begins with listening. We learn your business, your goals, and your concerns — then build a service plan that fits. No bloated retainers, no surprises on the invoice.
          </motion.p>
        </div>
      </section>

      {/* DETAILED SERVICES — alternating layout */}
      <section className="pb-24">
        <div className="container-x space-y-24">
          {services.map((s, i) => (
            <motion.div key={s.title} {...fadeUp} id={s.to.split("/").pop()} className={`grid lg:grid-cols-2 gap-12 lg:gap-16 items-center ${i % 2 ? "lg:[&>div:first-child]:order-2" : ""}`}>
              <div className="relative group">
                <div className="absolute -inset-3 bg-gradient-to-br from-gold/30 via-primary/10 to-transparent rounded-3xl blur-xl opacity-60 group-hover:opacity-90 transition-opacity duration-700" />
                <div className="absolute -top-3 -left-3 h-16 w-16 border-t-2 border-l-2 border-gold rounded-tl-2xl z-10" />
                <div className="absolute -bottom-3 -right-3 h-16 w-16 border-b-2 border-r-2 border-gold rounded-br-2xl z-10" />
                <div className="relative overflow-hidden rounded-2xl ring-1 ring-border">
                  <SmartImage src={s.img} alt={s.title} className="w-full aspect-[5/4] object-cover transition-transform duration-700 group-hover:scale-105" />
                </div>
              </div>
              <div>
                <div className="inline-flex items-center justify-center h-12 w-12 rounded-full bg-primary text-primary-foreground"><s.icon className="h-5 w-5" strokeWidth={1.5} /></div>
                <div className="mt-5 text-gold uppercase tracking-[0.25em] text-[11px]">{s.tagline}</div>
                <h3 className="font-display text-3xl md:text-4xl text-primary mt-2">{s.title}</h3>
                <p className="mt-4 text-muted-foreground leading-relaxed">{s.desc}</p>
                <ul className="mt-7 grid sm:grid-cols-2 gap-x-6 gap-y-3">
                  {s.items.map((it) => (
                    <li key={it} className="flex items-start gap-2.5 text-sm text-foreground/80"><CheckCircle2 className="h-4 w-4 text-gold mt-0.5 shrink-0" /> {it}</li>
                  ))}
                </ul>
                <div className="mt-9 flex flex-wrap items-center gap-5">
                  <Link to={s.to} className="btn-primary">Explore {s.title.replace(" Services", "")} <ArrowRight className="h-4 w-4" /></Link>
                  <a href={`tel:${site.phoneRaw}`} className="inline-flex items-center gap-2 text-sm text-primary hover:text-gold transition-colors">
                    <Phone className="h-4 w-4" /> {site.phone}
                  </a>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section className="py-20 bg-secondary/40">
        <div className="container-x">
          <motion.div {...fadeUp} className="text-center max-w-2xl mx-auto mb-12">
            <div className="text-gold uppercase tracking-[0.25em] text-xs mb-3">Why CCOB</div>
            <h2 className="font-display text-3xl md:text-4xl text-primary">A small-firm experience with big-firm discipline</h2>
          </motion.div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {why.map(([I, l]: any) => (
              <motion.div key={l} {...fadeUp} className="bg-card border border-border rounded-2xl p-6 hover:border-gold transition-colors">
                <div className="h-11 w-11 rounded-full grid place-items-center bg-background text-primary"><I className="h-5 w-5" strokeWidth={1.5} /></div>
                <div className="mt-4 font-display text-lg text-primary">{l}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="py-24">
        <div className="container-x">
          <motion.div {...fadeUp} className="max-w-2xl mb-14">
            <div className="text-gold uppercase tracking-[0.25em] text-xs mb-3">How We Work</div>
            <h2 className="font-display text-4xl md:text-5xl text-primary">A clear path from first call to ongoing partnership</h2>
          </motion.div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-7">
            {process.map((p) => (
              <motion.div key={p.step} {...fadeUp} className="relative">
                <div className="font-display text-5xl text-gold/40">{p.step}</div>
                <div className="mt-3 font-display text-xl text-primary">{p.t}</div>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{p.d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* INDUSTRIES */}
      <section className="py-20 bg-primary text-primary-foreground">
        <div className="container-x">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-5">
              <div className="text-gold uppercase tracking-[0.25em] text-xs mb-3">Industries We Serve</div>
              <h2 className="font-display text-3xl md:text-4xl">Specialized expertise across Central Florida sectors</h2>
            </div>
            <div className="lg:col-span-7 grid grid-cols-2 sm:grid-cols-3 gap-3">
              {industries.map((it) => (
                <motion.div {...fadeUp} key={it} className="border border-primary-foreground/15 rounded-xl px-4 py-3 text-sm hover:border-gold transition-colors">
                  {it}
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24">
        <div className="container-x text-center max-w-2xl mx-auto">
          <h2 className="font-display text-3xl md:text-4xl text-primary">Not sure which service fits?</h2>
          <p className="mt-4 text-muted-foreground">Book a free 30-minute discovery call. We'll listen, ask questions, and recommend the right path — even if it's not us.</p>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <a href={`tel:${site.phoneRaw}`} className="btn-primary"><Phone className="h-4 w-4" /> Call {site.phone}</a>
            <Link to="/contact" hash="consultation" className="btn-outline">Schedule a Consultation <ArrowRight className="h-4 w-4" /></Link>
          </div>
        </div>
      </section>
    </>
  );
}
